/*

    Complex image / video module with resizing based on scroll

    ## Resizing
    ------------
    Resizing is handled via intersection observer set to 15 steps.
    At each step (when intersecting / image is in view) the size is modifed.
    Scrolling down = image gets wider
    Scrolling up = image gets narrower

    Min-width is collected from image information
    Max-width is the max width of the window

    ## opacity
    ------------
    Opacity is controlled via an on-scroll event.
    The reason is that the image is now position-fixed, and no more thresholds are firing.

    When the image is at top of the screen it gets fixed.
    Based on scroll distance, the opacity is calculated.

    All of this works in reverse as well.

*/

import addScrollCallback from '../../foundation/js/addScrollCallback'

const el = '.kb-component--fullscreen-media'

function buildThresholdList() {
    let thresholds = []
    let numSteps = 201

    for (let i = 1; i <= numSteps; i++) {
        let ratio = i / numSteps
        thresholds.push(ratio)
    }

    return thresholds
}

// this modifier takes a look at screen width vs height.
// For 16:9 videos to properly display (without cropping) on 16:9 screens in full screen width.
// if there is enough height, the media is displayed in full pagewidth.
function maxWidthModifier() {
    const threshold16_9 = 1.8
    return document.documentElement.clientWidth /
        document.documentElement.clientHeight >
        threshold16_9
        ? 0.9
        : 1
}

// Value specifically requested per KBE0026-425
const MEDIA_FADE_FACTOR = 60

export default function () {
    const DOM = document.querySelectorAll(el)

    if (DOM && DOM.length) {
        DOM.forEach((FullscreenMediaDOM) => {
            // Remove the wrapping contentarea div
            FullscreenMediaDOM.parentElement.replaceWith(FullscreenMediaDOM)

            let currentScrollTopVal = window.scrollY
            let currentScrollTopValGeneral = window.scrollY
            let maxScreenWidth =
                document.documentElement.clientWidth * maxWidthModifier()
            let element = FullscreenMediaDOM.querySelector('#fullscreen-media')
            let minBoxWidth = element.getBoundingClientRect().width
            let boxHeight = element.getBoundingClientRect().height

            // censor element is the actual element that is being observed.
            // this way we have a container to apply a certain height to, and prevent the browser from getting the height calculations wrong.
            // this is what prevents flickering occuring in some cases.
            const sensor = FullscreenMediaDOM.querySelector(
                '#fullscreen-media-sensor'
            )
            sensor.style.height = '99vh'

            const elementDistanceFromDocumentTop = Math.round(
                window.scrollY + element.getBoundingClientRect().top
            )
            let scrolledFromTop =
                elementDistanceFromDocumentTop - window.scrollY > 0
                    ? 0
                    : (elementDistanceFromDocumentTop - window.scrollY) * -1

            // image resizing is handled inside the intersecton handler below.
            function handleIntersect(entries, observer) {
                entries.forEach((entry) => {
                    const currentRatio = entry.intersectionRatio
                    let scrollingDown = currentScrollTopVal < window.scrollY
                    let scrollingUp = currentScrollTopVal > window.scrollY

                    const animationControl = function (config) {
                        config = Object.assign(
                            {
                                currentRatio: currentRatio,
                                minBoxWidth: minBoxWidth,
                                maxScreenWidth: maxScreenWidth,
                                element: element,
                                ratioModifierFunction: null
                            },
                            config
                        )

                        // calculating new max-width based on ratio
                        let modifiedRatio = config.ratioModifierFunction()
                        let newMaxWidth =
                            config.minBoxWidth +
                            (config.maxScreenWidth - config.minBoxWidth) *
                                modifiedRatio

                        config.element.style.maxWidth =
                            currentRatio < 1
                                ? newMaxWidth <= maxScreenWidth
                                    ? newMaxWidth + 'px'
                                    : maxScreenWidth + 'px'
                                : maxScreenWidth + 'px'
                    }

                    if (scrollingDown) {
                        animationControl({
                            ratioModifierFunction: () => currentRatio
                        })
                    } else if (scrollingUp) {
                        animationControl({
                            ratioModifierFunction: () => currentRatio
                        })
                    }
                })
            }

            // image opacity is handled inside the on-scroll handler.
            addScrollCallback(() => {
                if (window.innerWidth < 769) {
                    element.style.opacity = 1
                    return true
                }

                const imageAtTopOfPage =
                    element.getBoundingClientRect().top <= 0
                const currentOpacity =
                    getComputedStyle(element).getPropertyValue('opacity')
                let newOpacity = currentOpacity

                let scrollingDown = currentScrollTopValGeneral < window.scrollY
                let scrollingUp = currentScrollTopValGeneral > window.scrollY
                scrolledFromTop =
                    elementDistanceFromDocumentTop - window.scrollY > 0
                        ? 0
                        : (elementDistanceFromDocumentTop - window.scrollY) * -1

                if (imageAtTopOfPage && scrollingDown) {
                    newOpacity =
                        1 -
                        ((scrolledFromTop * 100) / boxHeight < 100
                            ? (scrolledFromTop * 100) / boxHeight
                            : 100) /
                            MEDIA_FADE_FACTOR
                    currentScrollTopValGeneral = window.scrollY
                } else if (imageAtTopOfPage && scrollingUp) {
                    newOpacity =
                        1 -
                        ((scrolledFromTop * 100) / boxHeight < 100
                            ? (scrolledFromTop * 100) / boxHeight
                            : 100) /
                            MEDIA_FADE_FACTOR
                } else {
                    newOpacity = 1
                }

                element.style.opacity = newOpacity
                currentScrollTopValGeneral = window.scrollY
            })

            // Set things up
            window.addEventListener(
                'load',
                (event) => {
                    const fullscreenMediaElement = document.querySelector(
                        '#fullscreen-media-sensor'
                    )

                    let observer

                    let options = {
                        root: null,
                        rootMargin: '0px',
                        threshold: buildThresholdList()
                    }

                    observer = new IntersectionObserver(
                        handleIntersect,
                        options
                    )
                    observer.observe(fullscreenMediaElement)
                },
                false
            )
        })
    }
}
